
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import eventService from './eventService'

const toMillis = (value) => {
    if (!value) return value;
    if (typeof value === 'number') return value;
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (typeof value?.seconds === 'number') return value.seconds * 1000;
    return value;
};

const normalizeMeetdeskState = (desk) => {
    if (!desk || typeof desk !== 'object') return desk;
    return {
        ...desk,
        availabilityStart: toMillis(desk.availabilityStart),
        availabilityEnd: toMillis(desk.availabilityEnd),
        availableStart: toMillis(desk.availableStart),
        availableEnd: toMillis(desk.availableEnd),
        startDate: toMillis(desk.startDate),
        endDate: toMillis(desk.endDate),
        createdAt: toMillis(desk.createdAt),
        createDate: toMillis(desk.createDate),
        updateDate: toMillis(desk.updateDate),
        updatedAt: toMillis(desk.updatedAt),
    };
};

const initialState = {
    events: [],
    eventsPast: [],
    eventsFront: [],
    eventUsers: [],
    eventAnnouncements: [],
    event: null,
    eventNotes: [],
    eventAgendas: [],
    matchings: [],
    isError: false,
    isSuccess: false,
    isLoading: false,
    hasLoadedEvent: false,
    message: '',
    meetdesks: [],
    b2bMeetings: [],
    b2bSessions: [],
    b2bMatchings: [],
    myB2bMatchings: [],
}

export const eventSlice = createSlice({
    name: 'event',
    initialState,
    reducers: {
        reset: (state) => initialState
    },
    extraReducers: (builder) => {
        builder

            // getEvent
            .addCase(getEvent.pending, (state) => {
                state.isLoading = true
                state.isSuccess = false
                state.hasLoadedEvent = false
            })
            .addCase(getEvent.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.event = action.payload
                state.hasLoadedEvent = true
            })
            .addCase(getEvent.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
                state.hasLoadedEvent = true
            })


            // getEventUsers
            .addCase(getEventUsers.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getEventUsers.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.eventUsers = action.payload
                state.isError = false
            })
            .addCase(getEventUsers.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })



            // joinEvent
            .addCase(joinEvent.pending, (state) => {
                state.isLoading = true
            })
            .addCase(joinEvent.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.eventNotes.unshift(action.payload)
            })
            .addCase(joinEvent.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })



            // getMatchingsByMeeting
            .addCase(getMatchingsByMeeting.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getMatchingsByMeeting.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.matchings = action.payload
                state.isError = false
            })
            .addCase(getMatchingsByMeeting.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })


            // createMatching
            .addCase(createMatching.pending, (state) => {
                state.isLoading = true
            })
            .addCase(createMatching.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.matchings.unshift(action.payload)
            })
            .addCase(createMatching.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })


            // getMeetDesks
            .addCase(getMeetDesks.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getMeetDesks.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.meetdesks = Array.isArray(action.payload)
                    ? action.payload.map(normalizeMeetdeskState)
                    : []
            })
            .addCase(getMeetDesks.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })


            // getB2BMeetings
            .addCase(getB2BMeetings.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getB2BMeetings.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.b2bMeetings = action.payload
                state.isError = false
            })
            .addCase(getB2BMeetings.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })


            // getB2BSessions
            .addCase(getB2BSessions.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getB2BSessions.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.b2bSessions = action.payload
                state.isError = false
            })
            .addCase(getB2BSessions.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })


            // getB2BMatchings
            .addCase(getB2BMatchings.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getB2BMatchings.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.b2bMatchings = action.payload
                state.isError = false
            })
            .addCase(getB2BMatchings.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })


            // getMyB2BMatchings
            .addCase(getMyB2BMatchings.pending, (state) => {
                state.isLoading = true
            })
            .addCase(getMyB2BMatchings.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.myB2bMatchings = action.payload
                state.isError = false
            })
            .addCase(getMyB2BMatchings.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })


            // updateEvent
            .addCase(cancelMyB2BMatching.pending, (state) => {
                state.isLoading = true
            })
            .addCase(cancelMyB2BMatching.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
            })
            .addCase(cancelMyB2BMatching.rejected, (state, action) => {
                state.isLoading = false
                state.isSuccess = false
                state.message = action.payload
                state.isError = true
            })


    }
})

export const getEvent = createAsyncThunk('event/getEvent', async (id, thunkAPI) => {
    try {
        return await eventService.getEvent(id)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getEventUsers = createAsyncThunk('event/getEventUsers', async (eventId, thunkAPI) => {
    try {
        return await eventService.getEventUsers(eventId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const joinEvent = createAsyncThunk('event/joinEvent', async ({ userId, eventId }, thunkAPI) => {
    try {
        console.log('event/joinEvent: ' + userId);
        console.log('event/joinEvent: ' + eventId);
        return await eventService.joinEvent(eventId, userId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMatchingsByMeeting = createAsyncThunk('event/getMatchingsByMeeting', async (meetingId, thunkAPI) => {
    try {
        return await eventService.getMatchingsByMeeting(meetingId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const createMatching = createAsyncThunk('event/createMatching', async (matchingData, thunkAPI) => {
    try {
        return await eventService.createMatching(matchingData)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const getMeetDesks = createAsyncThunk('event/getMeetDesks', async (eventId, thunkAPI) => {
    try {
        return await eventService.getMeetDesks(eventId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})


export const getB2BMeetings = createAsyncThunk('event/getB2BMeetings', async (eventId, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        return await eventService.getB2BMeetings(eventId, userId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})


export const getB2BSessions = createAsyncThunk('event/getB2BSessions', async (eventId, thunkAPI) => {
    try {
        return await eventService.getB2BSessions(eventId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})


export const getB2BMatchings = createAsyncThunk('event/getB2BMatchings', async (payload, thunkAPI) => {
    try {
        if (payload && typeof payload === 'object') {
            const { eventId, startDate, endDate } = payload;
            return await eventService.getB2BMatchings(eventId, { startDate, endDate });
        }
        return await eventService.getB2BMatchings(payload);
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})


export const getMyB2BMatchings = createAsyncThunk('event/getMyB2BMatchings', async (eventId, thunkAPI) => {
    try {
        const userId = thunkAPI.getState().auth.user.uid
        return await eventService.getMyB2BMatchings(eventId, userId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})

export const cancelMyB2BMatching = createAsyncThunk('event/cancelMyB2BMatching', async ({ matchingId, meetingId }, thunkAPI) => {
    try {
        return await eventService.cancelMyB2BMatching(matchingId, meetingId)
    } catch (error) {
        const message = error.message
        return thunkAPI.rejectWithValue(message)
    }
})







export const { reset } = eventSlice.actions
export default eventSlice.reducer
